import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header, Footer } from "@/components/layout";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { buildMeta } from "@/lib/seo";
import { getGlobalDeals, type GlobalDeal } from "@/lib/global-deals.functions";
import { setEdgeCache } from "@/lib/ssr-cache";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { findAirport } from "@/lib/airports";
import { cityFromGen, prepFrom } from "@/lib/seo-routes";
import { REGIONS, regionOf, type Region } from "@/lib/regions";
import { PriceBadge } from "@/components/price-badge";
import { Globe, Plane, X, ChevronDown, ChevronRight, MapPin, ArrowRight } from "lucide-react";
import { trackAnywhereClick } from "@/lib/analytics";
import { ANYWHERE_ORIGINS } from "@/lib/anywhere-origins";

export const Route = createFileRoute("/gdziekolwiek/$from")({
  loader: async ({ params }) => {
    const code = params.from.toUpperCase();
    const origin = findAirport(code);
    if (!origin) throw notFound();
    await setEdgeCache({ sMaxAge: 1800, swr: 86400 });
    const deals = await getGlobalDeals({
      data: { origin: code, limit: 200 },
    }).catch(() => [] as GlobalDeal[]);
    return { deals, origin };
  },
  head: ({ loaderData }) => {
    const origin = loaderData?.origin;
    const minPrice = loaderData?.deals[0]?.priceNumeric;
    const cityLabel = origin ? `${origin.city}${origin.isMetro || origin.isCountry ? "" : ` (${origin.code})`}` : "Polska";
    const cityGen = origin ? cityFromGen(origin.city) : "Polski";
    const prep = origin ? prepFrom(origin.city) : "z";
    const priceSuffix = minPrice ? ` — od ${minPrice} zł` : "";
    const iata = origin?.code.toLowerCase() ?? "waw";
    const base = buildMeta({
      title: `Gdziekolwiek ${prep} ${cityGen}${priceSuffix} | Tanie Latanie`,
      description: `Najtańsze loty ${prep} ${cityGen} dokądkolwiek na świecie. Pogrupowane po krajach, ceny aktualizowane codziennie.`,
      path: `/gdziekolwiek/${iata}`,
      // Unikalny og:image per lotnisko (1536×1024, JPG). Pliki w public/og/anywhere/{iata}.jpg
      // są wygenerowane fotograficznie (panoramy miast) i automatycznie podpinane do og:image
      // oraz twitter:image przez buildMeta().
      image: `https://tanielatanie.pl/og/anywhere/${iata}.jpg`,
    });
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "Gdziekolwiek", path: "/gdziekolwiek" },
          { name: cityLabel, path: `/gdziekolwiek/${origin?.code.toLowerCase() ?? "waw"}` },
        ]),
      ],
    };
  },
  component: AnywhereFromPage,
});

type CountryGroup = {
  country: string;
  region: Region | null;
  minPrice: number;
  deals: GlobalDeal[];
};

function AnywhereFromPage() {
  const { deals, origin } = Route.useLoaderData();
  const cityLabel = origin.isMetro || origin.isCountry ? origin.city : `${origin.city} (${origin.code})`;
  const cityGen = cityFromGen(origin.city);
  const prep = prepFrom(origin.city);
  const cityGenLabel = origin.isMetro || origin.isCountry ? cityGen : `${cityGen} (${origin.code})`;

  // Grupowanie po kraju + max cena dla suwaka + dostępne regiony
  const { groups, priceCeiling, availableRegions } = useMemo(() => {
    const byCountry = new Map<string, GlobalDeal[]>();
    let max = 0;
    const regionSet = new Set<Region>();
    for (const d of deals) {
      if (d.priceNumeric > max) max = d.priceNumeric;
      const r = regionOf(d.toCountry);
      if (r) regionSet.add(r);
      const arr = byCountry.get(d.toCountry) ?? [];
      arr.push(d);
      byCountry.set(d.toCountry, arr);
    }
    const groups: CountryGroup[] = [...byCountry.entries()].map(([country, list]) => {
      const sorted = [...list].sort((a, b) => a.priceNumeric - b.priceNumeric);
      return {
        country,
        region: regionOf(country),
        minPrice: sorted[0]?.priceNumeric ?? 0,
        deals: sorted,
      };
    });
    groups.sort((a, b) => a.minPrice - b.minPrice);
    return {
      groups,
      priceCeiling: max > 0 ? Math.ceil(max / 100) * 100 : 2000,
      availableRegions: REGIONS.filter((r) => regionSet.has(r)),
    };
  }, [deals]);

  const [selectedRegions, setSelectedRegions] = useState<Set<Region>>(new Set());
  const [maxPrice, setMaxPrice] = useState<number>(priceCeiling);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filteredGroups = useMemo(() => {
    return groups
      .map((g) => ({
        ...g,
        deals: g.deals.filter((d) => d.priceNumeric <= maxPrice),
      }))
      .filter((g) => g.deals.length > 0)
      .filter((g) => {
        if (selectedRegions.size === 0) return true;
        return g.region ? selectedRegions.has(g.region) : false;
      });
  }, [groups, maxPrice, selectedRegions]);

  const toggleRegion = (r: Region) => {
    setSelectedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };
  const toggleExpanded = (country: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country); else next.add(country);
      return next;
    });
  };
  const resetFilters = () => {
    setSelectedRegions(new Set());
    setMaxPrice(priceCeiling);
  };
  const filtersActive = selectedRegions.size > 0 || maxPrice < priceCeiling;

  const totalDeals = filteredGroups.reduce((acc, g) => acc + g.deals.length, 0);

  return (
    <div className="min-h-screen">
      <Header />

      <section className="container mx-auto px-4 pt-6 pb-2">
        <Breadcrumbs
          items={[
            { name: "Strona główna", path: "/" },
            { name: "Gdziekolwiek", path: "/gdziekolwiek" },
            { name: cityLabel, path: `/gdziekolwiek/${origin.code.toLowerCase()}` },
          ]}
        />
      </section>

      <section className="container mx-auto px-4 pb-8 pt-6">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-medium uppercase tracking-widest">
            <Globe className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            Inspiracja podróżnicza
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            {prep === "ze" ? "Ze" : "Z"} {cityGenLabel} <span className="text-gradient-sunset">gdziekolwiek</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Nie wiesz dokąd lecieć? Wybraliśmy {deals.length} najtańszych
            kierunków {prep} {cityGenLabel} w najbliższych 30–90 dniach, posortowanych
            od najtańszego. Kliknij kraj, by zobaczyć wszystkie miasta.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs">
            <Link
              to="/gdziekolwiek"
              className="rounded-full bg-muted/30 px-3 py-1.5 font-medium hover:bg-muted/50"
            >
              ← Zmień lotnisko wylotu
            </Link>
          </div>
        </div>
      </section>

      {deals.length > 0 && (
        <section className="container mx-auto px-4 pb-6">
          <div className="glass rounded-xl p-4 md:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Filtry
              </div>
              {filtersActive && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 rounded-full bg-muted/30 px-3 py-1 text-xs font-medium hover:bg-muted/50"
                >
                  <X className="h-3 w-3" aria-hidden="true" /> Wyczyść
                </button>
              )}
            </div>

            <div className="mt-4">
              <label htmlFor="maxPrice" className="mb-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Maksymalna cena</span>
                <span className="font-semibold text-gradient-sunset">do {maxPrice} zł</span>
              </label>
              <input
                id="maxPrice"
                type="range"
                min={100}
                max={priceCeiling}
                step={50}
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                style={{
                  background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((maxPrice - 100) / Math.max(1, priceCeiling - 100)) * 100}%, var(--muted) ${((maxPrice - 100) / Math.max(1, priceCeiling - 100)) * 100}%, var(--muted) 100%)`,
                }}
                className="price-range h-2 w-full cursor-pointer appearance-none rounded-full accent-primary"
              />
            </div>

            {availableRegions.length > 1 && (
              <div className="mt-4">
                <div className="mb-2 text-xs text-muted-foreground">
                  Region {selectedRegions.size > 0 && <span className="text-primary">({selectedRegions.size} wybranych)</span>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {availableRegions.map((r) => {
                    const active = selectedRegions.has(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleRegion(r)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${active ? "bg-primary text-primary-foreground shadow-glow" : "bg-muted/30 text-foreground hover:bg-muted/50"}`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="container mx-auto px-4 pb-16">
        {deals.length === 0 ? (
          <div className="glass mx-auto max-w-xl rounded-lg p-8 text-center text-muted-foreground">
            Nie mamy jeszcze cen z {cityLabel}. Spróbuj{" "}
            <Link to="/gdziekolwiek" className="text-primary hover:underline">innego lotniska</Link>{" "}
            lub wróć za chwilę.
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="glass mx-auto max-w-xl rounded-lg p-8 text-center text-muted-foreground">
            Brak kierunków pasujących do filtrów.{" "}
            <button onClick={resetFilters} className="text-primary hover:underline">Wyczyść filtry</button>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filteredGroups.length}</span> krajów ·{" "}
              <span className="font-semibold text-foreground">{totalDeals}</span> kierunków
            </div>
            <ol className="grid gap-3 md:grid-cols-2">
              {filteredGroups.map((g) => {
                const isOpen = expanded.has(g.country);
                return (
                  <li key={g.country} className="glass overflow-hidden rounded-xl">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(g.country)}
                      className="flex w-full items-center justify-between gap-3 p-4 text-left transition hover:bg-accent/20"
                      aria-expanded={isOpen}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <MapPin className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-display text-base font-bold">{g.country}</div>
                          <div className="text-xs text-muted-foreground">
                            {g.deals.length} {g.deals.length === 1 ? "miasto" : g.deals.length < 5 ? "miasta" : "miast"}
                            {g.region && <> · {g.region}</>}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="text-right">
                          <div className="text-[10px] uppercase text-muted-foreground">od</div>
                          <div className="whitespace-nowrap text-base font-semibold text-gradient-sunset">{g.minPrice} zł</div>
                        </div>
                        {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </button>
                    {isOpen && (
                      <ul className="divide-y divide-border/40 border-t border-border/40 bg-background/40">
                        {g.deals.map((d) => (
                          <li key={d.from + d.to}>
                            <Link
                              to="/loty/$from/$to"
                              params={{ from: d.from.toLowerCase(), to: d.to.toLowerCase() }}
                              onClick={() => trackAnywhereClick({
                                origin: origin.code,
                                destination: d.to,
                                country: g.country,
                                price: d.priceNumeric,
                              })}
                              className="group flex items-center gap-3 px-4 py-3 transition hover:bg-accent/30"
                              title={`Tanie loty ${d.fromCity} → ${d.toCity}`}
                            >
                              <Plane className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold group-hover:text-primary">
                                  {d.toCity}
                                </div>
                                <div className="font-mono text-[10px] text-muted-foreground">
                                  {d.from} → {d.to}
                                </div>
                              </div>
                              {d.badge && <PriceBadge badge={d.badge} size="xs" />}
                              <div className="whitespace-nowrap text-sm font-semibold text-gradient-sunset">
                                {d.price}
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </section>

      {/* Interlinking SEO: linki do siostrzanych landingów /gdziekolwiek/{iata}.
          Pomaga Google odkrywać cały hub (Anywhere) z każdej podstrony i
          rozprowadza link equity między 12 landingami per-lotnisko. */}
      <section className="container mx-auto max-w-5xl px-4 pb-12">
        <h2 className="mb-4 font-display text-xl font-bold">Sprawdź z innych lotnisk</h2>
        <ul className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {ANYWHERE_ORIGINS.filter((o) => o.code !== origin.code).map((o) => (
            <li key={o.code}>
              <Link
                to="/gdziekolwiek/$from"
                params={{ from: o.code.toLowerCase() }}
                className="glass group flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm transition-all hover:shadow-glow"
                title={`Najtańsze loty z ${o.label} — gdziekolwiek`}
              >
                <span className="truncate font-medium group-hover:text-primary">{o.label}</span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary opacity-50 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/gdziekolwiek"
              className="glass group flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm transition-all hover:shadow-glow"
              title="Wszystkie lotniska wylotu"
            >
              <span className="truncate font-medium text-primary">Wszystkie lotniska</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:opacity-100" />
            </Link>
          </li>
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Każde lotnisko ma własny ranking najtańszych krajów — porównaj, skąd dany kierunek jest tańszy.
        </p>
      </section>

      <Footer />
    </div>
  );
}
